import { Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import ChangeLogDetailsDialog from '../components/change_logs/ChangeLogDetailsDialog';
import ChangeLogsFilters from '../components/change_logs/ChangeLogsFilters';
import ChangeLogsTable from '../components/change_logs/ChangeLogsTable';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import changeLogsService from '../services/change-logs-service';

export default function ChangeLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableNames, setTableNames] = useState([]);
  const [changeSources, setChangeSources] = useState([]);
  const [changedByUsers, setChangedByUsers] = useState([]);

  const [filters, setFilters] = useState({
    table_name: '',
    action: '',
    change_source: '',
    changed_by: '',
    date_filter_start: '',
    date_filter_end: ''
  });

  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });

  const fetchLogs = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await changeLogsService.search({
          page,
          limit,
          ...filters
        });
        if (response.success) {
          setLogs(response.data || []);
          setPagination({
            ...response.pagination,
            limit,
            totalPages: response.pagination?.totalPages || 1,
            total: response.total || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch change logs:', error);
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, filters]
  );

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [tableNamesRes, changeSourcesRes, changedByRes] = await Promise.all([
        changeLogsService.getDistinctTableNames(),
        changeLogsService.getDistinctChangeSources(),
        changeLogsService.getDistinctChangedBy()
      ]);
      if (tableNamesRes.success) setTableNames(tableNamesRes.data || []);
      if (changeSourcesRes.success)
        setChangeSources(changeSourcesRes.data || []);
      if (changedByRes.success) setChangedByUsers(changedByRes.data || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchFilterOptions();
  }, [fetchLogs, fetchFilterOptions]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  return (
    <div style={{ marginTop: 40, padding: 20 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Change Logs (Superadmin Only)
      </Typography>

      <ChangeLogsFilters
        filters={filters}
        tableNames={tableNames}
        changeSources={changeSources}
        changedByUsers={changedByUsers}
        onFilterChange={handleFilterChange}
      />

      <ChangeLogsTable
        logs={logs}
        loading={loading}
        onViewDetails={handleViewDetails}
      />

      <PaginatedControls
        pagination={pagination}
        setPagination={setPagination}
        packagesLength={logs.length}
        searchValue=""
        filters={[]}
        handleSearch={fetchLogs}
        fetchAll={fetchLogs}
      />

      <ChangeLogDetailsDialog
        open={detailsOpen}
        log={selectedLog}
        onClose={handleCloseDetails}
      />
    </div>
  );
}
